import styled from "styled-components";

import type { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import {
  getMergedDeadSpans,
  getMergedMerchandisableWidth,
  getShelfItemWidth,
  getShelfItemXOffsets,
  hasObstructionSpans,
} from "@CommonUtils/pog/shelfMerchandising";
import {
  DEFAULT_DRAWN_DIVIDER_HEIGHT,
  getDividerDrawSize,
  getShelfItemDividerSlots,
} from "@CommonUtils/pog/shelfDividers";
import {
  getRunItemHeight,
  SHELF_DIVIDER_COLOR,
  SHELF_DIVIDER_Z_INDEX,
} from "../../common/shelfDividerDraw";

/**
 * How wide each item's slot is on a run an obstruction blocks: from the previous item's right
 * edge to this item's right edge, at the offsets the Konva editor projects. Null when no member
 * states strips, so legacy shelves keep their flex row exactly as before.
 */
export const getItemSlotWidths = (
  shelves: readonly PlanogramShelf[],
  items: readonly PlanogramItem[],
): number[] | null => {
  if (!shelves.some(hasObstructionSpans)) return null;

  const itemWidths = items.map(getShelfItemWidth);
  const offsets = getShelfItemXOffsets({
    itemWidths,
    merchandisableWidth: getMergedMerchandisableWidth(shelves),
    overflowPolicy: "extend-on-overflow",
    deadSpans: getMergedDeadSpans(shelves),
  });

  let previousRight = 0;
  return offsets.map((offset, index) => {
    const right = offset + itemWidths[index];
    const width = right - previousRight;
    previousRight = right;
    return width;
  });
};

/** Holds one item at its slot's right end; the item's own left dropper fills the gap before it. */
export const ItemSlot = styled.div<{ $align: "flex-start" | "flex-end" }>`
  display: flex;
  flex: none;
  align-items: ${({ $align }) => $align};
`;

/** A divided run as the DOM renderer draws it, in shelf units from the run's left edge. */
export interface DividedRunLayout {
  /** Each item's slot, from the previous slot's right edge to its own right edge. */
  slotWidths: number[];
  /** Inside each slot, the space right of the product: its trailing divider. */
  trailingWidths: number[];
  /** Every divider of the run, left edge and true width. */
  dividers: { key: string; x: number; width: number }[];
  /** How tall the dividers are drawn: the run's tallest item. */
  dividerHeight: number;
}

/**
 * Where a divided run's items and dividers go, at the offsets the Konva renderer and the model
 * place them (`getShelfItemDividerSlots` laid out by `getShelfItemXOffsets`). Null when no member
 * shelf has active dividers, so divider-free runs keep their flex row exactly as before.
 */
export const getDividedRunLayout = (
  shelves: readonly PlanogramShelf[],
  items: readonly PlanogramItem[],
): DividedRunLayout | null => {
  const slots = getShelfItemDividerSlots(items);
  if (!slots) return null;

  const itemWidths = slots.map((slot) => slot.width);
  const offsets = getShelfItemXOffsets({
    itemWidths,
    merchandisableWidth: getMergedMerchandisableWidth(shelves),
    overflowPolicy: "extend-on-overflow",
    deadSpans: getMergedDeadSpans(shelves),
    // A divided shelf a pole cuts lays each piece out on its own (DEV-3536).
    pieces: slots.map((slot) => slot.piece),
  });

  let previousRight = 0;
  const slotWidths = offsets.map((offset, index) => {
    const right = offset + itemWidths[index];
    const width = right - previousRight;
    previousRight = right;
    return width;
  });
  const trailingWidths = slots.map((slot) => slot.width - slot.productX - slot.productWidth);
  const dividers = slots.flatMap((slot, index) => {
    const width = Number(slot.dividers?.width ?? 0);
    return slot.dividers
      ? slot.dividerXs.map((x, dividerIndex) => ({
          key: `${items[index].uniqueId}-divider-${dividerIndex}`,
          x: offsets[index] + x,
          width,
        }))
      : [];
  });

  const itemHeight = getRunItemHeight(items);
  const firstDividers = slots.find((slot) => slot.dividers)?.dividers ?? null;
  const stubHeight = firstDividers
    ? getDividerDrawSize(firstDividers, 0, DEFAULT_DRAWN_DIVIDER_HEIGHT).height
    : 0;
  return {
    slotWidths,
    trailingWidths,
    dividers,
    dividerHeight: itemHeight > 0 ? itemHeight : stubHeight,
  };
};

/**
 * A divider drawn over the run, above product images and highlight overlays: positioned in
 * screen px by the caller, bottom on the shelf, never thinner than `MIN_DIVIDER_SCREEN_PX`.
 */
export const ShelfDividerMark = styled.div`
  position: absolute;
  bottom: 0;
  z-index: ${SHELF_DIVIDER_Z_INDEX};
  background-color: ${SHELF_DIVIDER_COLOR};
  pointer-events: none;
`;
